const DEFAULT_MAX_EDGE = 1600;
const DEFAULT_QUALITY = 0.82;
const OPTIMIZE_ABOVE_BYTES = 900 * 1024;

const nextPaint = () => new Promise((resolve) => {
  if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
    resolve();
    return;
  }
  window.requestAnimationFrame(() => window.setTimeout(resolve, 0));
});

const canvasToBlob = (canvas, type, quality) => new Promise((resolve) => {
  canvas.toBlob(resolve, type, quality);
});

const loadImageElement = (file) => new Promise((resolve, reject) => {
  const objectUrl = window.URL.createObjectURL(file);
  const image = new window.Image();
  image.onload = () => {
    window.URL.revokeObjectURL(objectUrl);
    resolve(image);
  };
  image.onerror = () => {
    window.URL.revokeObjectURL(objectUrl);
    reject(new Error('Image could not be decoded'));
  };
  image.src = objectUrl;
});

export function proofUploadErrorMessage(error) {
  const response = error?.response;
  const payload = response?.data || {};
  const nestedError = payload?.error || {};
  const code = String(nestedError?.code || payload?.code || error?.code || '').toUpperCase();
  if (Number(response?.status || 0) === 413 || code === 'PAYLOAD_TOO_LARGE') {
    return 'Screenshot is too large. Please choose an image under 30 MB.';
  }

  const detail = payload?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail?.message === 'string') return detail.message;
  if (typeof nestedError?.message === 'string' && nestedError.message.trim()) {
    return nestedError.message;
  }
  if (typeof payload?.message === 'string' && payload.message.trim()) return payload.message;

  return 'Deposit screenshot could not be submitted. Keep this page open and tap Submit again.';
}

/**
 * Shrink large payment screenshots before upload without blocking the first
 * UI paint. Unsupported formats/browsers safely fall back to the source file;
 * the backend still validates and re-encodes every proof.
 */
export async function optimizeProofImage(file, options = {}) {
  if (!file || !String(file.type || '').toLowerCase().startsWith('image/')) return file;
  if (Number(file.size || 0) <= OPTIMIZE_ABOVE_BYTES) return file;
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return file;
  }

  const maxEdge = Number(options.maxEdge || DEFAULT_MAX_EDGE);
  const quality = Number(options.quality || DEFAULT_QUALITY);
  let bitmap;
  try {
    // Let React show "Optimizing" before image decoding starts on mobile.
    await nextPaint();
    bitmap = typeof window.createImageBitmap === 'function'
      ? await window.createImageBitmap(file)
      : await loadImageElement(file);
    const sourceWidth = bitmap.width || bitmap.naturalWidth;
    const sourceHeight = bitmap.height || bitmap.naturalHeight;
    const longest = Math.max(sourceWidth, sourceHeight);
    const scale = longest > maxEdge ? maxEdge / longest : 1;
    const width = Math.max(1, Math.round(sourceWidth * scale));
    const height = Math.max(1, Math.round(sourceHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) return file;
    context.fillStyle = '#FFFFFF';
    context.fillRect(0, 0, width, height);
    context.drawImage(bitmap, 0, 0, width, height);

    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (!blob || blob.size <= 0 || blob.size >= file.size) return file;
    const sourceName = String(file.name || 'payment-proof').replace(/\.[^.]+$/, '');
    return new File([blob], `${sourceName}.jpg`, {
      type: 'image/jpeg',
      lastModified: file.lastModified || Date.now(),
    });
  } catch {
    // HEIC and a few vendor image formats cannot be decoded by every browser.
    // Upload the original and let Pillow perform the canonical conversion.
    return file;
  } finally {
    if (bitmap && typeof bitmap.close === 'function') bitmap.close();
  }
}
